import { Formik, getIn } from 'formik';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Col, Grid, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  changeTypeVisitTrackingAPI, changeVisitTrackingTypeAPI,
  discontinuationVisitTracking,
  endPatientInProtocolVisitTrackingAPI,
  exportVisitTrackingAPI,
  getVisitTrackingAttachmentFormEndpoint,
  getVisitTrackingForm, getVisitTrackingForms, lostToFollowUpVisitTracking, publishVisitTracking,
  removeVisitTrackingAPI,
  reSyncVisitTrackingAPI,
  returnDrugsVisitTracking,
  rollbackVisitStatusAPI,
  saveUploadVisitTrackingAPI,
  saveVisitTracking, screenFailureVisitTracking, skipVisitTracking,
  uploadVisitTrackingAttachmentFormEndpoint,
  withoutDrugsVisitTracking,
  getPendingAttachmentsAPI,
} from '../../../api/protocol';

import { PendingAttachmentsModal } from '../../../components/Protocols/Tracking/PendingAttachmentsModal';

import { RemoveVisitModal } from '../../../components/Protocols/Tracking/RemoveVisitModal';

import { AuditModal, CreateAuditModal } from '../../../components/AuditTrail/AuditModal';
import { WithAuth } from '../../../components/Authorization/WithAuthProtocol';
import { useAuthProtocol } from '../../../components/Authorization/useAuthorization';
import { DocumentViewer } from '../../../components/CustomControls/DocViewer/DocumentViewer';
import { useFiles } from '../../../components/CustomControls/Files/Hooks';
import { SignWithOutInfo } from '../../../components/DigitalSign/Sign';
import { DynamicForm } from '../../../components/DynamicForm/DynamicForm';
import { MedicalRecord, OnSite, Phone, VideoCall } from '../../../components/Icons/Icons';
import { ModalConfirm } from '../../../components/Modals/ModalConfirm';
import { ModalSpinner } from '../../../components/Modals/ModalSpinner';
import { HandleApiError, HandleError, HandleMessageError, HandleMessagesError, HandleGroupedMessagesError } from '../../../components/Notifications/APIErrorHandler';
import { extractApiErrorCodes } from '../../../components/Utils/extractApiErrorCodes';
import { Success } from '../../../components/Notifications/Notifications';
import { PatientStatusInProtocol } from '../../../components/Protocols/Patients';
import { AssignBranchModal, PatientBranchBadge, useBranches, usePatientBranch } from '../Branches';
import { FormActions, FormMenu, VisitStatus } from '../../../components/Protocols/Tracking/Forms';
import { ChangeVisitType, MissingRequiredFieldsModal } from '../../../components/Protocols/Tracking/Modals';
import { Slider } from '../../../components/Slider/Slider';
import { Spinner } from '../../../components/Utils/Loaders';
import { getFormatValidationSchema, getMissingRequiredFieldsViaSchema, getValidationSchema } from '../../../components/Validations/FormValidationSchema';
import { formStatus, visitStatus, visitTypeEnum } from "../../../variables/Enums";
import { AuditTrail } from "../../Admin/AuditTrail";
import { EntryItemsListTabs } from '../../MedicalRecords/MedicalRecordItems/EntryItemsList';
import { SignatureModal } from '../../Signature/Signature';
import DocumentNotesContainer from '../DocumentNotes/DocumentNotes';
import { EvolutionsContainer } from '../Evolutions/Evolutions';
import { useAudits } from '../Hooks';
import MonitorNotesContainer from '../MonitorNotes/MonitorNotes';
import ProtocolContext from '../ProtocolContext/ProtocolContext';
import EditingUser from '../TrackingForms/EditingUserNotification';
import { useEditingUser } from '../TrackingForms/useEditingUser';
import { MissingConsents } from "./VisitTracking/MissingConsents";
import { getDuplicateFileNames } from './Services';

const getVisitType = (visitType) => {
  switch (visitType) {
    case visitTypeEnum.OnSite:
      return <OnSite />
    case visitTypeEnum.Telephone:
      return <Phone />
    case visitTypeEnum.Remote:
      return <VideoCall />
    default:
      return "";
  }
}

export const VisitTracking = ({ protocolId, patientId, visitId, onVisitTrackingChanged }) => {
  let history = useHistory();
  const { t } = useTranslation();
  const [isAuthorized, isAuthLoading, currentUser] =
    useAuthProtocol(protocolId);
  const settingsStore = useSelector((state) => state.settings);
  const context = useContext(ProtocolContext);
  const [, getBlobAPI, postAPI] = useFiles();
  const { isUserEditing, onCurrentEditorChange, onSelectedFormChange } =
    useEditingUser();

  const [modal, setModal] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [hasVisitTracking, setHasVisitTracking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [forms, setForms] = useState([]);
  const [visitTracking, setVisitTracking] = useState(null);
  const [signStatus, setSignStatus] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formId, setFormId] = useState(null); // Usado para preseleccionar un Form al hacer un Reload. 
  const [warnedFormIds, setWarnedFormIds] = useState(new Set());
  const [pendingMissingFieldsSave, setPendingMissingFieldsSave] = useState(null);
  const [patientInfo, setPatientInfo] = useState(null);
  const [visitHasBranches, setVisitHasBranches] = useState(false);
  const [patientBranch, , reloadBranch] = usePatientBranch(protocolId, patientId);
  const [protocolBranches] = useBranches(protocolId);
  const [, , getLastChange] = useAudits("VisitTrackingForm", selectedForm?.id, hasVisitTracking);
  const [, , getLastVisitTrackingChange] = useAudits("VisitTracking", visitTracking?.id, hasVisitTracking);
  const [reload, setReload] = useState(false);
  const [evolutions, setEvolutions] = useState([]);
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const returnUrl = urlParams.get("returnUrl");
  const [missingConsents, setMissingConsents] = useState([]);

  const formsWithMissingFields = useMemo(() =>
    (forms || [])
      .filter(f => f.extraFormType !== 0)
      .filter(f => getMissingRequiredFieldsViaSchema(manageConditionalFieldsInForm(f)).length > 0)
      .map(f => ({ id: f.id, title: f.title })),
    [forms] 
  );

  useEffect(() => {
    if (visitTracking && onVisitTrackingChanged) {
      onVisitTrackingChanged();
    }
  }, [visitTracking?.status]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setReload(false);
      try {
        const response = await getVisitTrackingForms(
          protocolId,
          visitId,
          patientId
        );

        var visitTracking = response.data;
        console.log(visitTracking)
        if (!visitTracking)
          HandleMessagesError("protocolErrors.visitTracking_NotFound");

        let formAux = visitTracking.forms[0];
        if (
          urlParams.has("formId") &&
          visitTracking.forms.find((x) => x.id == urlParams.get("formId"))
        )
          formAux = visitTracking.forms.find(
            (x) => x.id == urlParams.get("formId")
          );

        if (formId && visitTracking.forms.find((x) => x.id == formId))
          formAux = visitTracking.forms.find((x) => x.id == formId);

        selectForm(formAux);

        setEvolutions(visitTracking.visit.evolutions);
        setForms(visitTracking.forms);
        setVisitTracking(visitTracking.visit);
        setHasVisitTracking(visitTracking.hasVisitTracking);
        setSignStatus(visitTracking.signStatus);
        setVisitHasBranches(visitTracking.visitHasBranches ?? false);
        setPatientInfo({
          patientNumber: visitTracking.patientNumber,
          patientInProtocolStatus: visitTracking.patientInProtocolStatus,
          medicalRecordNumber: visitTracking.medicalRecordNumber,
          fullName: visitTracking.patientFullName,
        });
        setMissingConsents(visitTracking.missingConsents);
        setIsLoading(false);
      } catch (error) {
        console.log(error);
        HandleApiError(error);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [visitId, reload]);

  //#region private Functions

  //#region ConditionalFields
  /**
   * Setea visibilidad de campos del formulario, teniendo en cuenta los Si/No Condicionales (booleanFields)
   * @param {PatientForm} form
   */
  function manageConditionalFieldsInForm(form) {
    let formAux = JSON.parse(JSON.stringify(form));
    let fields = formAux.fields.map((x) => {
      if (typeof x.hide === "undefined")
        return Object.assign({}, x, { hide: false });
      return x;
    });

    // Resuelvo conditionals.
    var conditionalFields = fields.filter(
      (x) =>
        (x._t === "BooleanField" || x._t === "PayableField") && x.conditional
    );
    conditionalFields.forEach((conditionalField) => {
      fields
        .filter((field) => conditionalField.conditional.includes(field.id))
        .forEach((fieldWithCondition) => {
          if (conditionalField.value === false) fieldWithCondition.hide = true;
          if (conditionalField.value === true) fieldWithCondition.hide = false;
        });
    });

    formAux.fields = fields;

    return formAux;
  }

  function selectForm(form) {
    if (form)
      setSelectedForm(manageConditionalFieldsInForm(form));
    else
      setSelectedForm({});
  }

  //#endregion

  //#region Commons
  const handleSelectForm = (formId, isDirty) => {
    if (
      !isDirty ||
      (isDirty &&
        window.confirm(t("protocolNotifications.visitTracking_unsavedChanges")))
    ) {
      setIsLoading(true);
      let selectedForms = forms.filter((x) => {
        return x.id === formId;
      });
      let formAux = JSON.parse(
        JSON.stringify(selectedForms.length > 0 ? selectedForms[0] : {})
      );
      formAux.text = selectedForm.text;

      selectForm(formAux);
      if (onSelectedFormChange(formId)) {
        setFormId(formId);
        setReload(true);
      } else {
        setFormId(null);
      }

      setIsLoading(false);
    }
  };
  const _closeModal = (formikBag) => {
    setModal(null);
    if (formikBag && formikBag.setSubmitting) formikBag.setSubmitting(false);
  };
  const _updateData = (visit, updatedForm) => {
    if (!updatedForm || !updatedForm.id) {
      window.location.reload(false);
      return;
    }

    updatedForm.text = "";
    let formsCopy = JSON.parse(JSON.stringify(forms));
    var index = formsCopy.findIndex((x) => x.id === updatedForm.id);
    formsCopy.splice(index, 1, updatedForm); //Reemplazo form.
    setVisitTracking(visit);
    setEvolutions(visit.evolutions);
    setForms(formsCopy);
    selectForm(updatedForm);

    _closeModal();
    Success("protocolNotifications.visitTracking_FormSaved");
  }
  const handleAuditTrail = () => {
    setModal(
      <AuditModal
        onClose={_closeModal}
        title={t("auditTrail.audit")}
      >
        {t("auditTrail.field")}:
        <AuditTrail entity="VisitTrackingForm" entityId={selectedForm.id} fields={selectedForm && selectedForm.fields} />

        {t("auditTrail.evolution")}:
        <AuditTrail entity="VisitTracking" entityId={visitTracking.id} />
      </AuditModal>
    )
  }
  const handleViewFile = async (attachName, field) => {
    try {
      var res = await getBlobAPI(attachName, getVisitTrackingAttachmentFormEndpoint(protocolId, visitId, selectedForm.id, attachName));
      if (!res) return;
      setModal(<DocumentViewer
        file={res?.blob}
        fileName={res?.fileName}
        isLoading={res?.isLoading}
        date={field?.validated}
        userId={field?.validatedBy}
        onClose={_closeModal}
      />)
    }
    catch (error) {
      console.error(error);
      HandleApiError(error);
    }
  }

  /**
   * Muestra modal para firmar documento
   * @param {*} confirmFunc Funcion a llamar si la firma fue correcta
   * @param {*} props FormikBags
   */
  const _signatureModal = (confirmFunc, props) => {
    if (settingsStore.settings.digitalSignEnabled)
      setModal(<SignatureModal
        isShowing={true}
        hide={() => _closeModal(props)}
        onSubmit={(pin) => confirmFunc(pin, props)}
      />);

    else
      confirmFunc({ password: "" }, props)
  }
  //#endregion

  //#region SaveVisit
  const validateBeforeSave = (formikProps) => {
    const { values, setFormikState } = formikProps;

    try {
      getFormatValidationSchema().validateSync(values, { abortEarly: false });
      return true;

    } catch (yupError) {
      const errors = {};
      const touched = {};

      if (yupError.inner) {
        yupError.inner.forEach(error => {
          errors[error.path] = error.message;
          touched[error.path] = true;
          const fieldLabel = getIn(formikProps.values, error.path.replace("value", "label"));
          HandleMessageError(fieldLabel, `${error.message}`);
        });
      }

      setFormikState(prevState => ({
        ...prevState,
        errors: errors,
        touched: touched,
        // Aumentar submitCount hace que ErrorMessage ignore 
        // validaciones automáticas momentáneamente en v1.x
        submitCount: prevState.submitCount + 1
      }));

      return false;
    }
  };

  const _saveVisit = async (formVisitTracking, formikProps) => {

    try {
      if (formikProps.isSubmitting)
        return;

      formikProps.setSubmitting(true);
      setModal(<ModalSpinner isShowing={true} hide={null} />);

      const res = await saveVisitTracking(
        protocolId,
        visitId,
        patientId,
        patientInfo.medicalRecordNumber,
        formVisitTracking
      );
      const form = res.data.form;
      const visit = res.data.visit;

        // Visit guardado (200), pero el batch de inventario falló post-save: aviso traducido.
        // inventoryErrors viene camelCase (System.Text.Json): { error, message }.
        const inventoryErrors = res.data.inventoryErrors;
        if (Array.isArray(inventoryErrors) && inventoryErrors.length > 0)
            HandleMessagesError("inventory.errorCodes.title",
                inventoryErrors.map(e => t(`inventory.errorCodes.${e.error}`, { defaultValue: e.message ?? e.error })));

        // El formulario se guarda igual (no bloqueante): si algún AP/EA/Medicación/Procedimiento
        // cargado se superpone en fechas con algo ya existente para el paciente, se avisa acá.
        const duplicatedItems = res.data.duplicatedItems;
        if (duplicatedItems) {
            const conflictMessages = [
                ...(duplicatedItems.adverseEventConflicts || []).map(x => ({ name: x.adverseEffect, from: x.from })),
                ...(duplicatedItems.drugConflicts || []).map(x => ({ name: x.drug, from: x.from })),
                ...(duplicatedItems.procedureConflicts || []).map(x => ({ name: x.procedure, from: x.from })),
            ].map(x => t("protocolNotifications.visitTracking_DuplicatedTimeRange_item", { name: x.name, from: x.from }));

            if (conflictMessages.length > 0)
                HandleMessagesError("protocolNotifications.visitTracking_DuplicatedTimeRange_title", conflictMessages);
        }
      
      // Upload attachments if any.
      const fieldsWithFiles = formVisitTracking.patientForm.fields.filter(
        (x) => x._t === "FileUploadField" && x.value?.file
      );
      const componentsWithFiles = formVisitTracking.patientForm.fields
        .filter((x) => x._t === "ArrayField")
        .map((array) => {
          return array.components
            .map((component) => {
              return component.fields
                .map(
                  (field) =>
                    field._t === "FileUploadField" && field !== null && field
                )
                .flat();
            })
            .flat();
        })
        .flat()
        .filter((x) => x.value?.file); // filtro por los nuevos solamente.
      const files = fieldsWithFiles.concat(componentsWithFiles);

      if (files && files.length > 0) {
        try {
          const attachs = files.map(x => x.value);
          postAPI(attachs, uploadVisitTrackingAttachmentFormEndpoint(protocolId, visitId, form.id, patientId, patientInfo.medicalRecordNumber))
            .then(() => {
              if (visit.numberInProtocol !== patientId) {
                history.push(`/admin/protocols/${protocolId}/visitTracking/${visitId}/patient/${visit?.numberInProtocol}`);
              }
              getVisitTrackingForm(protocolId, visitId, form.id)
                .then(resForm => {
                  // Actualizo el form en FrontEnd.
                  _updateData(visit, resForm?.data);
                }).catch((error) => {
                  console.log(error)
                  window.location.reload(false)
                })
            }).catch((error) => { console.log(error) });
        }
        catch (error) {
          console.log(error.response);
        }
      }
      else {
        if (visit.numberInProtocol !== patientId) {
          history.push(`/admin/protocols/${protocolId}/visitTracking/${visitId}/patient/${visit?.numberInProtocol}`);
        }
        _updateData(visit, form);
      }
    }
    catch (error) {
        console.log(error);
        const codes = extractApiErrorCodes(error);
        if (codes.length > 0)
            HandleMessagesError("inventory.errorCodes.title", codes.map(c => t(`inventory.errorCodes.${c.code}`, { defaultValue: c.message ?? c.code })));
        else
            HandleApiError(error);
      _closeModal();
      formikProps.setSubmitting(false);
    }
  }

  const _continueWithSave = (formVisitTracking, formikProps) => {
    const duplicates = getDuplicateFileNames(formVisitTracking.fields);
    if (duplicates.length > 0) {
      HandleError(t('protocolErrors.visitTracking_duplicateFileNames', { fileNames: duplicates.join(', ') }), false);
      return;
    }

    if (visitTracking.status !== visitStatus.Started || formVisitTracking.status !== formStatus.Initial) {
      setModal(<CreateAuditModal
        onClose={_closeModal}
        onSave={(reason, comments) => _saveVisit({ patientForm: formVisitTracking, audit: { reason: reason, comments: comments } }, formikProps)}
      />)
    }
    else {
      _saveVisit({ patientForm: formVisitTracking }, formikProps);
    }
  };

  const handleSaveVisit = (formVisitTracking, formikProps) => {
    if (!formikProps.dirty) return; // CU-3: no guardar sin cambios

    const okToSave = validateBeforeSave(formikProps, formVisitTracking);
    if (!okToSave) return;

    // CU-1: advertir si hay campos requeridos incompletos (solo formularios de contenido)
      if (formVisitTracking.extraFormType !== 0) {
        const missingFields = getMissingRequiredFieldsViaSchema(manageConditionalFieldsInForm(formVisitTracking));
      if (missingFields.length > 0 && !warnedFormIds.has(formVisitTracking.id)) {
        setPendingMissingFieldsSave({ missingFields, formVisitTracking, formikProps });
        return;
      }
    }

    _continueWithSave(formVisitTracking, formikProps);
  };

  const handleConfirmMissingFields = () => {
    const { formVisitTracking, formikProps } = pendingMissingFieldsSave;
    setWarnedFormIds(prev => new Set([...prev, formVisitTracking.id]));
    setPendingMissingFieldsSave(null);
    _continueWithSave(formVisitTracking, formikProps);
  };
  //#endregion

  //#region SaveUpload
  const _saveUploadVisit = async (formVisitTracking, formikProps) => {
    try {
      if (formikProps.isSubmitting) return;

      formikProps.setSubmitting(true);
      setModal(<ModalSpinner isShowing={true} hide={null} />);

      const res = await saveUploadVisitTrackingAPI(
        protocolId,
        visitId,
        patientId,
        patientInfo.medicalRecordNumber,
        formVisitTracking
      );
      const form = res.data.form;
      const visit = res.data.visit;

        // Visit guardado (200), pero el batch de inventario falló post-save: aviso traducido.
        // inventoryErrors viene camelCase (System.Text.Json): { error, message }.
        const inventoryErrors = res.data.inventoryErrors;
        if (Array.isArray(inventoryErrors) && inventoryErrors.length > 0)
            HandleMessagesError("inventory.errorCodes.title",
                inventoryErrors.map(e => t(`inventory.errorCodes.${e.error}`, { defaultValue: e.message ?? e.error })));
        
        // Upload attachments. 
      const fieldsWithFiles = formVisitTracking.patientForm.fields.filter(x => x._t === "FileUploadField" && x.value?.file);
      const componentsWithFiles = formVisitTracking.patientForm.fields.filter(x => x._t === "ArrayField")
        .map(array => {
          return array.components.map(component => {
            return component.fields.map(field => field._t === "FileUploadField" && field !== null && field).flat();
          }).flat();
        }).flat().filter(x => x.value?.file); // filtro por los nuevos solamente. 
      const files = fieldsWithFiles.concat(componentsWithFiles);

      if (files && files.length > 0) {
        try {
          const attachs = files.map((x) => x.value);
          postAPI(
            attachs,
            uploadVisitTrackingAttachmentFormEndpoint(
              protocolId,
              visitId,
              form.id,
              patientId,
              patientInfo.medicalRecordNumber
            )
          )
            .then(() => {
              getVisitTrackingForm(protocolId, visitId, form.id)
                .then((resForm) => {
                  // Actualizo el form en FrontEnd.
                  _updateData(visit, resForm?.data);
                })
                .catch((error) => {
                  console.log(error);
                  window.location.reload(false);
                });
            })
            .catch((error) => {
              console.log(error);
            });
        } catch (error) {
          console.log(error.response);
        }
      } else {
        _updateData(visit, form);
      }
    } catch (error) {
      _closeModal();
      formikProps.setSubmitting(false);
      console.log(error);
      HandleApiError(error);
    } finally {
    }
  };

  const handleSaveUploadVisit = (formVisitTracking, formikProps) => {
    const duplicates = getDuplicateFileNames(formVisitTracking.fields);
    if (duplicates.length > 0) {
      HandleError(t('protocolErrors.visitTracking_duplicateFileNames', { fileNames: duplicates.join(', ') }), false);
      return;
    }

    if (visitTracking.status !== visitStatus.Started || formVisitTracking.status !== formStatus.Initial) {
      setModal(<CreateAuditModal
        onClose={_closeModal}
        onSave={(reason, comments) => _saveUploadVisit({ patientForm: formVisitTracking, audit: { reason: reason, comments: comments } }, formikProps)}
      />)
    }
    else {
      _saveUploadVisit({ patientForm: formVisitTracking }, formikProps);
    }
  }
  //#endregion

  //#region PublishVisit
  const _publishVisit = async (digitalSign, actions) => {
    try {
      setModal(<ModalSpinner isShowing={true} hide={null} />);
      const res = await publishVisitTracking(protocolId, visitId, patientId, digitalSign);
      _closeModal(actions);
      setVisitTracking(res.data);
      Success("protocolNotifications.visitTracking_VisitPublished");
      // La visita se publicó, pero la H.C. rechazó la sincronización: se avisa para que re-sincronicen.
      if (res.headers?.["x-sync-failed"] === "true")
        HandleMessageError("protocolNotifications.visitTracking_syncFailed");
      setReload(true);
    }
    catch (error) {
      _closeModal(actions);
      console.log(error);
      HandleApiError(error);
    }
  }
  const handleSubmitFormik = async (_formVisitTracking, actions) => {
    try {
        const res = await getPendingAttachmentsAPI(protocolId, visitId, patientId);
        if (res.data.hasPending) {
            setModal(
            <PendingAttachmentsModal
                pendingData={res.data}
                currentUser={currentUser?.sub}
                protocolId={protocolId}
                visitId={visitId}
                patientId={patientId}
                medicalRecordNumber={patientInfo.medicalRecordNumber}
                onSaveUpload={saveUploadVisitTrackingAPI}
                onContinue={() => {
                setModal(null);
                _signatureModal(_publishVisit, actions);
                }}
                onCancel={() => {
                setModal(null);
                actions.setSubmitting(false);
                }}
            />
            );
        } else {
            _signatureModal(_publishVisit, actions);
        }
    } catch (error) {
      HandleApiError(error);
      actions.setSubmitting(false);
    }
  }
    const handleSubmitForm = (evt, formikBag) => {
        evt.preventDefault();
        if (!formikBag.dirty || (formikBag.dirty && window.confirm(t("protocolNotifications.visitTracking_unsavedChanges")))) {
            // CU-4: validar campos requeridos en todos los formularios de contenido con detalle de campo
            const formsWithDetails = forms
            .filter(f => f.extraFormType !== 0)
            .reduce((acc, f) => {
                const missingFields = getMissingRequiredFieldsViaSchema(manageConditionalFieldsInForm(f));
                if (missingFields.length > 0) acc.push({ id: f.id, title: f.title, missingFields });
                return acc;
            }, []);

            if (formsWithDetails.length > 0) {
                const showErrorValidations = formsWithDetails.some(f => f.id === formikBag.values.id);
                HandleGroupedMessagesError("protocolErrors.visitTracking_requiredFieldsError",
                    formsWithDetails.map(f => ({ id: f.id, title: f.title, items: f.missingFields.map(mf => mf.title) })));
                if (showErrorValidations) {
                    formikBag.submitForm();
                }  
            } else {
                formikBag.submitForm();
            }
        }   
    }
  //#endregion

  //#region SkipVisit
  const handleSkipVisit = (formikBag) => {
    if (!formikBag.dirty || (formikBag.dirty && window.confirm(t("protocolNotifications.visitTracking_unsavedChanges")))) {
      setModal(<ModalConfirm
        onCancel={() => _closeModal(formikBag)}
        onConfirm={() => confirmSkipVisit(formikBag)}
        title={t(`protocolNotifications.visitTracking_confirmSkip_title`)}
        description={t(`protocolNotifications.visitTracking_confirmSkip_description`)}
      />)
    }
  }
  const confirmSkipVisit = (formikBag) => {
    if (settingsStore.settings.digitalSignEnabled)
      setModal(<SignatureModal
        isShowing={true}
        isLoading={isLoading}
        hide={() => _closeModal(formikBag)}
        onSubmit={(pin) => _publishSkipVisit(pin, formikBag)}
      />);

    else
      _publishSkipVisit({ password: "" }, formikBag)
  }
  const _publishSkipVisit = async (digitalSign, formikBag) => {
    try {
      setIsLoading(true);
      const res = await skipVisitTracking(protocolId, visitId, patientId, digitalSign);
      setReload(true);
      Success("protocolNotifications.visitTracking_FormSaved");
    }
    catch (error) {
      console.log(error);
      HandleApiError(error);
    } finally {
      _closeModal(formikBag);
      setIsLoading(false);
    }
  }
  //#endregion

  //#region ScreenFailure
  const handleScreenFailure = (formikBag) => {
    if (!formikBag.dirty || (formikBag.dirty && window.confirm(t("protocolNotifications.visitTracking_unsavedChanges")))) {
      setModal(<ModalConfirm
        onCancel={() => _closeModal(formikBag)}
        onConfirm={() => auditScreenFailure(formikBag)}
        title={t(`protocolNotifications.visitTracking_screenFailure_title`)}
        description={t(`protocolNotifications.visitTracking_screenFailure_description`)}
      />)
    }
  }
  const auditScreenFailure = (formikBag) => {
    if (visitTracking.status === visitStatus.Completed || visitTracking.status === visitStatus.Changed)
      setModal(<CreateAuditModal
        onClose={() => _closeModal(formikBag)}
        onSave={(reason, comments) => confirmScreenFailure(formikBag, { reason: reason, comments: comments })}
      />)
    else
      confirmScreenFailure(formikBag, null, { password: "" })
  }
  const confirmScreenFailure = (formikBag, auditTrail) => {
    if (settingsStore.settings.digitalSignEnabled)
      setModal(<SignatureModal
        isShowing={true}
        isLoading={isLoading}
        hide={() => _closeModal(formikBag)}
        onSubmit={(pin) => _publishScreenFailure(formikBag, auditTrail, pin)}
      />);
    else
      _publishScreenFailure(formikBag, auditTrail, { password: "" })
  }
  const _publishScreenFailure = async (formikBag, auditTrail, digitalSign) => {
    try {
      setIsLoading(true);
      await screenFailureVisitTracking(protocolId, visitId, patientId, { auditTrail, digitalSign });
      Success("protocolNotifications.visitTracking_FormSaved");
      setReload(true);
    }
    catch (error) {
      console.log(error);
      HandleApiError(error);
    } finally {
      _closeModal(formikBag);
      setIsLoading(false);
    }
  }
  //#endregion

  //#region LostToFollowUp
  const handleLostToFollowUp = (formikBag) => {
    if (!formikBag.dirty || (formikBag.dirty && window.confirm(t("protocolNotifications.visitTracking_unsavedChanges")))) {
      setModal(<ModalConfirm
        onCancel={() => _closeModal(formikBag)}
        onConfirm={() => confirmLostToFollowUp(formikBag)}
        title={t(`protocolNotifications.visitTracking_lostToFollowUp_title`)}
        description={t(`protocolNotifications.visitTracking_lostToFollowUp_description`)}
      />)
    }
  }
  const confirmLostToFollowUp = (formikBag) => {
    if (settingsStore.settings.digitalSignEnabled)
      setModal(<SignatureModal
        isShowing={true}
        isLoading={isLoading}
        hide={() => _closeModal(formikBag)}
        onSubmit={(pin) => _publishLostToFollowUp(pin, formikBag)}
      />);
    else
      _publishLostToFollowUp({ password: "" }, formikBag)
  }
  const _publishLostToFollowUp = async (digitalSign, formikBag) => {
    try {
      setIsLoading(true);
      await lostToFollowUpVisitTracking(protocolId, visitId, patientId, { digitalSign });
      Success("protocolNotifications.visitTracking_FormSaved");
      setReload(true);
    }
    catch (error) {
      console.log(error);
      HandleApiError(error);
    }
    finally {
      _closeModal(formikBag);
      setIsLoading(false);
    }
  }
  //#endregion

  //#region Discontinuation
  const handleDiscontinuation = (formikBag) => {
    if (
      !formikBag.dirty ||
      (formikBag.dirty &&
        window.confirm(t("protocolNotifications.visitTracking_unsavedChanges")))
    ) {
      setModal(
        <ModalConfirm
          onCancel={() => _closeModal(formikBag)}
          onConfirm={() => creationEarlyTerminationVisit(formikBag)}
          title={t(`protocolNotifications.visitTracking_discontinuation_title`)}
          description={t(
            `protocolNotifications.visitTracking_discontinuation_description`
          )}
        />
      );
    }
  }
  const creationEarlyTerminationVisit = (formikBag) => {
    setModal(<ModalConfirm
      customButtons={<React.Fragment>
        <button className='btn btn-lg btn-default btn-margin' onClick={() => _closeModal(formikBag)}>{t("buttons.cancel")}</button>
        <button className='btn btn-lg btn-info btn-margin' onClick={() => auditDiscontinuation(formikBag, false)}>{t("buttons.no")}</button>
        <button className='btn btn-lg btn-success btn-margin' onClick={() => auditDiscontinuation(formikBag, true)}>{t("buttons.yes")}</button>
      </React.Fragment>}
      title={t(`protocolNotifications.visitTracking_earlyTermination_title`)}
      description={t(`protocolNotifications.visitTracking_earlyTermination_description`)}
    />)
  }
  const auditDiscontinuation = (formikBag, earlyTerminationWillBeCreated) => {
    if (visitTracking.status === visitStatus.Completed || visitTracking.status === visitStatus.Changed)
      setModal(<CreateAuditModal
        onClose={() => _closeModal(formikBag)}
        onSave={(reason, comments) => confirmDiscontinuation(formikBag, earlyTerminationWillBeCreated, { reason: reason, comments: comments })}
      />)
    else
      confirmDiscontinuation(formikBag, earlyTerminationWillBeCreated, null);
  }
  const confirmDiscontinuation = (formikBag, earlyTerminationWillBeCreated, auditTrail) => {
    if (settingsStore.settings.digitalSignEnabled)
      setModal(<SignatureModal
        isShowing={true}
        isLoading={isLoading}
        hide={() => _closeModal(formikBag)}
        onSubmit={(pin) => _publishDiscontinuation(formikBag, earlyTerminationWillBeCreated, auditTrail, pin)}
      />);
    else
      _publishDiscontinuation(formikBag, earlyTerminationWillBeCreated, auditTrail, { password: "" });
  }
  const _publishDiscontinuation = async (formikBag, earlyTerminationWillBeCreated, auditTrail, digitalSign) => {
    try {
      setIsLoading(true);
      await discontinuationVisitTracking(protocolId, visitId, patientId, earlyTerminationWillBeCreated, { auditTrail, digitalSign });
      Success("protocolNotifications.visitTracking_VisitPublished");
      setReload(true);
    }
    catch (error) {
      console.log(error);
      HandleApiError(error);
    }
    finally {
      _closeModal(formikBag);
      setIsLoading(false);
    }
  }
  //#endregion

  //#region WithoutDrugs
  const handleWithoutDrugs = async (formikBag) => {
    setModal(
      <ModalConfirm
        title={t("protocolNotifications.visitTracking_withoutDrugs_title")}
        description={t(
          "protocolNotifications.visitTracking_withoutDrugs_description"
        )}
        onCancel={() => _closeModal()}
        onConfirm={() => _setWithoutDrugs(formikBag)}
      />
    );
  };
  const _setWithoutDrugs = async (formikBag) => {
    try {
      setIsLoading(true);
      await withoutDrugsVisitTracking(protocolId, visitId, patientId);
      Success("protocolNotifications.visitTracking_FormSaved");
      setReload(true);
    } catch (error) {
      console.log(error);
      HandleApiError(error);
    } finally {
      _closeModal(formikBag);
      setIsLoading(false);
    }
  };
  //#endregion

  //#region ReturnDrugs
  const handleReturnDrugs = async (formikBag) => {
    setModal(
      <ModalConfirm
        title={t("protocolNotifications.visitTracking_returnDrugs_title")}
        description={t(
          "protocolNotifications.visitTracking_returnDrugs_description"
        )}
        onCancel={() => _closeModal(formikBag)}
        onConfirm={() => _setReturnDrugs(formikBag)}
      />
    );
  };
  const _setReturnDrugs = async (formikBag) => {
    try {
      setIsLoading(true);
      await returnDrugsVisitTracking(protocolId, visitId, patientId);
      Success("protocolNotifications.visitTracking_FormSaved");
      setReload(true);
    } catch (error) {
      console.log(error);
      HandleApiError(error);
    } finally {
      _closeModal(formikBag);
      setIsLoading(false);
    }
  };
  //#endregion

  //#region EndProtocol
  const handleEndPatientInProtocol = (formikBag) => {
    if (!formikBag.dirty || (formikBag.dirty && window.confirm(t("protocolNotifications.visitTracking_unsavedChanges")))
    ) {
      setModal(
        <ModalConfirm
          onCancel={() => _closeModal(formikBag)}
          onConfirm={() => auditEndPatientInProtocol(formikBag)}
          title={t(
            "protocolNotifications.visitTracking_endPatientInProtocol_title"
          )}
          description={t(
            "protocolNotifications.visitTracking_endPatientInProtocol_description"
          )}
        />
      );
    }
  }
  const auditEndPatientInProtocol = (formikBag,) => {
    if (visitTracking.status === visitStatus.Completed || visitTracking.status === visitStatus.Changed)
      setModal(<CreateAuditModal
        onClose={() => _closeModal(formikBag)}
        onSave={(reason, comments) => confirmEndPatientInProtocol(formikBag, { reason: reason, comments: comments })}
      />)
    else
      confirmScreenFailure(formikBag, null, { password: "" })
  }
  const confirmEndPatientInProtocol = (formikBag, auditTrail) => {
    if (settingsStore.settings.digitalSignEnabled)
      setModal(<SignatureModal
        isShowing={true}
        isLoading={isLoading}
        hide={() => { setModal(null); formikBag.setSubmitting(false); }}
        onSubmit={(pin) => _publishEndPatientInProtocol(formikBag, auditTrail, pin)}
      />);
    else
      _publishEndPatientInProtocol(formikBag, auditTrail, { password: "" })
  }
  const _publishEndPatientInProtocol = async (formikBag, auditTrail, digitalSign) => {
    try {
      setIsLoading(true);
      await endPatientInProtocolVisitTrackingAPI(protocolId, visitId, patientId, { auditTrail, digitalSign });
      Success("protocolNotifications.visitTracking_FormSaved");
      setReload(true);
    }
    catch (error) {
      console.log(error);
      HandleApiError(error);
    }
    finally {
      _closeModal(formikBag);
      setIsLoading(false);
    }
  }
  //#endregion

  //#region RollbackVisitStatus
  const handleRollbackVisitStatus = (formikBag) => {
    if (
      !formikBag.dirty ||
      (formikBag.dirty &&
        window.confirm(t("protocolNotifications.visitTracking_unsavedChanges")))
    ) {
      setModal(
        <ModalConfirm
          onCancel={() => _closeModal(formikBag)}
          onConfirm={() => confirmRollbackVisitStatus(formikBag)}
          title={t(
            "protocolNotifications.visitTracking_rollbackVisitStatus_title"
          )}
          description={t(
            "protocolNotifications.visitTracking_rollbackVisitStatus_description"
          )}
        />
      );
    }
  };
  const confirmRollbackVisitStatus = (formikBag) => {
    setModal(
      <CreateAuditModal
        onClose={_closeModal}
        onSave={(reason, comments) =>
          _publishRollbackVisitStatus(
            { audit: { reason: reason, comments: comments } },
            formikBag
          )
        }
      />
    );
  };
  const _publishRollbackVisitStatus = async (audit, formikBag) => {
    try {
      setIsLoading(true);
      await rollbackVisitStatusAPI(protocolId, visitId, patientId, audit);
      Success("protocolNotifications.visitTracking_rollbackDone");
      setReload(true);
    } catch (error) {
      console.log(error);
      HandleApiError(error);
    } finally {
      _closeModal(formikBag);
      setIsLoading(false);
    }
  };
  //#endregion

  //#region Resync
  const handleReSync = () => {
    setModal(<ModalConfirm
      onCancel={_closeModal}
      onConfirm={() => _signatureModal(_confirmReSync)}
      title={t("protocolNotifications.visitTracking_reSync_title")}
      description={t("protocolNotifications.visitTracking_reSync_description")}
    />);
  }
  const _confirmReSync = async (digitalSign) => {
    try {
      setIsLoading(true);
      await reSyncVisitTrackingAPI(protocolId, visitId, patientId, digitalSign);
      Success("protocolNotifications.visitTracking_reSyncOk");
    }
    catch (error) {
      console.log(error);
      HandleApiError(error);
    } finally {
      _closeModal();
      setIsLoading(false);
    }
  }
  //#endregion

  //#region ChangeVisitType
  const handleChangeVisitTrackingType = (formikBag) => {
    setModal(
      <ModalConfirm
        title={t(
          "protocolNotifications.visitTracking_changeVisitTrackingType_title"
        )}
        description={t(
          "protocolNotifications.visitTracking_changeVisitTrackingType_description"
        )}
        onCancel={() => _closeModal(formikBag)}
        onConfirm={() => _setChangeVisitTrackingType(formikBag)}
      />
    );
  };
  const _setChangeVisitTrackingType = async (formikBag) => {
    try {
      setIsLoading(true);
      await changeVisitTrackingTypeAPI(
        protocolId,
        visitId,
        patientId,
        visitTracking.entryId,
        visitTracking.id
      );
      Success("protocolNotifications.visitTracking_changeTypeOk");
      history.push(
        `/admin/protocols/${protocolId}/visitTrackingExtra/${visitTracking.id}/patient/${patientId}`
      );
    } catch (error) {
      console.log(error);
      HandleApiError(error);
    } finally {
      _closeModal(formikBag);
      setIsLoading(false);
    }
  };
  //#endregion

  //#region ChangeType
  const handleChangeType = (formikBag) => {
    setModal(
      <ChangeVisitType
        visit={t("protocolNotifications.visitTracking_changeType_title")}
        onClose={() => _closeModal(formikBag)}
        onChange={(type) => _changeType(type, formikBag)}
      />
    );
  };
  const _changeType = async (type, formikBag) => {
    try {
      setIsLoading(true);
      const res = await changeTypeVisitTrackingAPI(
        protocolId,
        visitId,
        patientId,
        type
      );
      setVisitTracking(res.data);
      Success("protocolNotifications.visitTracking_FormSaved");
    } catch (error) {
      console.log(error);
      HandleApiError(error);
    } finally {
      _closeModal(formikBag);
      setIsLoading(false);
    }
  };
  //#endregion

  //#region RemoveVisit
  const handleRemoveVisit = (formikBag) => {
    setModal(
      <RemoveVisitModal
        onCancel={() => _closeModal(formikBag)}
        onConfirm={(reason) => _confirmRemoveVisit(reason)}
      />
    );
  };
  const _confirmRemoveVisit = async (reason) => {
    try {
      setIsLoading(true);
      _closeModal();
      await removeVisitTrackingAPI(protocolId, visitId, patientId, reason);
      Success("protocolNotifications.visitTracking_removeVisitOk");
      history.push(`/admin/protocols/${protocolId}`);
    } catch (error) {
      console.log(error);
      HandleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };
  //#endregion


  //#region Export
  const _export = async (pin) => {
    try {
      setModal(<ModalSpinner isShowing={true} hide={null} />);

      const response = await exportVisitTrackingAPI(
        protocolId,
        visitTracking?.id,
        patientId,
        pin?.password
      );
      //const blob = new Blob([response.data], { type: "text/html" });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";

      var fileName = "PatientVisit.zip";
      var disposition = response.headers['content-disposition'];
      if (disposition && disposition.indexOf('attachment') !== -1) {
        var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        var matches = filenameRegex.exec(disposition);
        if (matches != null && matches[1]) fileName = matches[1].replace(/['"]/g, '');
      }
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      _closeModal();
    }
    catch (error) {
      setIsLoading(false);
      _closeModal();
      console.log(error);
      HandleApiError(error);
    }
  }
  const handleExport = async () => {
    if (settingsStore.settings.digitalSignEnabled)
      setModal(<SignatureModal
        isShowing={true}
        hide={() => { setModal(null); }}
        onSubmit={(pin) => _export(pin)}
      />);

    else
      _export("")
  }
  //#endregion

  const handleAssignBranch = () => {
    setModal(
      <AssignBranchModal
        isShowing={true}
        hide={() => setModal(null)}
        protocolId={protocolId}
        numberInProtocol={patientId}
        isChange={false}
        onSuccess={() => { setModal(null); reloadBranch(true); }}
      />
    );
  };

  const handleBack = (evt) => {
    evt.preventDefault();
    if (returnUrl) {
      history.push(decodeURIComponent(returnUrl));
    } else {
      history.push(`/admin/protocols/${protocolId}/section/visittracking`);
    }
  };
  //#endregion  

  const handleCurrentEditorChange = (editor) => {
    setReload(onCurrentEditorChange(editor, selectedForm?.id));
  };

  //if (!visitTracking || !selectedForm || !forms || isLoading || settingsStore.isLoading || isAuthLoading)
  if (!visitTracking || isLoading || settingsStore.isLoading || isAuthLoading)
    return <Spinner />;

  const isDisabled = !isAuthorized(["VisitTracking.Add"]) || context.isDisabled || !hasVisitTracking || isUserEditing;
  const isUploadEnabled = isAuthorized(["VisitTracking.UploadFile"]) && !context.isDisabled && hasVisitTracking && !isUserEditing;
  const showStatus = ((!isAuthorized(["VisitTracking.Add"]) && visitTracking?.status !== visitStatus.Started) || isAuthorized(["VisitTracking.Add"]));
  const canAssignBranch = protocolBranches.length > 0 &&
    !visitHasBranches &&
    !patientBranch &&
    visitTracking != null &&
    [visitStatus.Started, visitStatus.Changed, visitStatus.Completed, visitStatus.Skipped, visitStatus.AutoCompleted].includes(visitTracking.status);

  return (
    <>
      {modal}
      {pendingMissingFieldsSave && (
        <MissingRequiredFieldsModal
          missingFields={pendingMissingFieldsSave.missingFields}
          onConfirm={handleConfirmMissingFields}
          onCancel={() => setPendingMissingFieldsSave(null)}
        />
      )}
      <Formik
        initialValues={selectedForm}
        validationSchema={selectedForm.extraFormType !== 0 && getValidationSchema}
        onSubmit={handleSubmitFormik}
        enableReinitialize={true}
        validateOnBlur={true}
        validateOnChange={true}
      >
        {props => (
          <form onReset={props.handleReset} onSubmit={(evt) => handleSubmitForm(evt, props)}>
            <Row>
              <Col md={12}>
                <EditingUser currentUser={currentUser} currentFormId={selectedForm?.id} isDirty={props.dirty} onCurrentEditorChange={handleCurrentEditorChange} />
              </Col>
            </Row>
            <MissingConsents
              protocolId={protocolId}
              patientId={patientId}
              missingConsents={missingConsents}
              setMissingConsents={setMissingConsents}
            />
            <Row>
              <Col md={4} lg={3} className="sticky">
                <Row>
                  <Col md={12}>
                    <FormMenu
                      forms={forms}
                      onClick={(formId) => handleSelectForm(formId, props.dirty)}
                      selectedFormId={selectedForm?.id}
                      formsWithErrors={formsWithMissingFields}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={12} className="sticky">
                    <FormActions
                      visit={visitTracking}
                      hasVisitTracking={hasVisitTracking}
                      forms={forms}
                      patientInProtocolStatus={patientInfo?.patientInProtocolStatus}
                      onBack={handleBack}
                      onSave={() => handleSaveVisit(props.values, props)}
                      onSaveUpload={() => handleSaveUploadVisit(props.values, props)}
                      onSkip={() => handleSkipVisit(props)}
                      onExport={handleExport}
                      onLostToFollowUp={() => handleLostToFollowUp(props)}
                      onScreenFailure={() => handleScreenFailure(props)}
                      onDiscontinuation={() => handleDiscontinuation(props)}
                      onWithoutDrugs={() => handleWithoutDrugs(props)}
                      onReturnDrugs={() => handleReturnDrugs(props)}
                      onChangeType={() => handleChangeType(props)}
                      onEndPatientInProtocol={() => handleEndPatientInProtocol(props)}
                      onRollbackVisitStatus={() => handleRollbackVisitStatus(props)}
                      onChangeVisitTrackingType={() => handleChangeVisitTrackingType(props)}
                      onReSync={handleReSync}
                      onAuditTrail={handleAuditTrail}
                      onBackToMR={() => history.push(`/admin/medicalRecords/${patientInfo?.medicalRecordNumber}`)}
                      onRemove={() => handleRemoveVisit(props)}
                      onAssignBranch={handleAssignBranch}
                      canAssignBranch={canAssignBranch}
                      isSubmitting={props.isSubmitting}
                      disabled={isUserEditing}
                      hasMissingConsents={missingConsents?.length > 0}
                      disabledSave={!props.dirty}
                    />
                  </Col>
                </Row>
              </Col>
              <Col md={4} lg={5}>
                {showStatus && visitTracking && <VisitStatus status={visitTracking?.status} date={visitTracking?.created} />}
                <DynamicForm
                  {...props}
                  currentUser={currentUser?.sub}
                  patientId={visitTracking.patientId}
                  patientNumber={patientInfo.patientNumber}
                  visitId={visitId}
                  protocolId={protocolId}
                  onDownload={handleViewFile}
                  isDisabled={isDisabled}
                  isUploadEnabled={isUploadEnabled}
                  getLastChange={getLastChange}
                />
                {
                  (visitTracking?.status === visitStatus.Completed || visitTracking?.status === visitStatus.Skipped || visitTracking?.status === visitStatus.Failed) &&
                  <SignWithOutInfo signedBy={selectedForm.createdById} created={selectedForm.created} locale={true} />
                }
                <WithAuth protocolId={protocolId} requiredPermission={["InternalNotes.List"]}>
                  {
                    hasVisitTracking &&
                    <DocumentNotesContainer
                      protocolId={protocolId}
                      documentId={selectedForm.id}
                      visitId={visitId}
                      patientId={patientInfo.patientNumber}
                      type="VisitTracking"
                      noteId={urlParams.get("noteId")}
                    />
                  }
                </WithAuth>
              </Col>
              <Col md={4} lg={4} className="sticky">
                <button type="button"
                  className="btn btn-primary btn-fill btn-full"
                  onClick={() => { setShowHistory(!showHistory) }}
                  style={{ marginBottom: '22px', textAlign: 'center' }}>
                  <MedicalRecord /> {t("protocols.visitTracking.viewMR")}
                </button>

                <EvolutionsContainer
                  {...props}
                  visitType="VisitTracking"
                  protocolId={protocolId}
                  visitId={visitId}
                  patientId={patientId}
                  medicalRecordNumber={patientInfo.medicalRecordNumber}
                  visitTrackingStatus={visitTracking.status}
                  setVisitTracking={setVisitTracking}
                  data={evolutions}
                  audit={getLastVisitTrackingChange("VisitText")}
                  isDisabled={isDisabled}
                  title={<>{getVisitType(visitTracking.visitType)} {t("protocols.visitTracking.patientEvolution")}</>}
                  subtitle={<><PatientStatusInProtocol status={patientInfo?.patientInProtocolStatus} /> {patientInfo?.patientNumber} {patientInfo?.fullName}{protocolBranches.length > 0 && <PatientBranchBadge protocolId={protocolId} numberInProtocol={patientId} branch={patientBranch} onBranchChanged={() => reloadBranch(true)} />}</>}
                />
                {
                  (visitTracking?.status === visitStatus.Completed || visitTracking?.status === visitStatus.Skipped || visitTracking?.status === visitStatus.Failed) &&
                  <SignWithOutInfo signedBy={visitTracking.createdById} created={visitTracking.created} locale={true} />
                }
                {
                  /*settingsStore.settings.digitalSignEnabled &&
                  <DigitalSign signedBy={visitTracking.digitalSign?.signedBy} signStatus={signStatus} signedDate={visitTracking.digitalSign?.signedDate} />*/
                }
                <WithAuth protocolId={protocolId} requiredPermission={["ExternalNotes.List"]}>
                  {
                    hasVisitTracking &&
                    <MonitorNotesContainer
                      protocolId={protocolId}
                      documentId={selectedForm.id}
                      visitId={visitId}
                      patientId={patientInfo.patientNumber}
                      type="VisitTracking"
                    />
                  }
                </WithAuth>
              </Col>
              <Slider isOpen={showHistory}>
                <Grid fluid>
                  <button type="button"
                    className="btn btn-primary btn-fill btn-full"
                    onClick={() => { setShowHistory(!showHistory) }}
                    style={{ margin: '22px 0', textAlign: 'center' }}>
                    <MedicalRecord /> {t("protocols.visitTracking.hideMR")}
                  </button>
                  <EntryItemsListTabs medicalRecordNumber={visitTracking.patientId} inProtocol />
                </Grid>
              </Slider>
            </Row>
          </form>
        )}
      </Formik>
    </>
  );
}